import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useFormikContext } from 'formik';
import React, { FC, useState } from 'react';
import { toast } from "react-toastify";
import { COMMAND_STATE } from '../../constants/command-status';
import Commands from '../../constants/commands';
import { AccountSubCmds } from '../../constants/sub-commands';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Button } from '../../components/button/Button';
import { ExecuteCommand } from '../../models/ExecuteCommand';
import { executeCommand } from '../../services/executeCommandService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { requiredValidation, validateIntegerWithinBounds, validatePassword, validateUsername } from '../../utilities/Validation';
import ActivityFrame from '../activity-frame/activity-frame';
import { useDialog } from "../dialog/DialogContext";
import Label from '../label/label';

interface LDAPAccountDetailsProps {
    isAccountPopulated: boolean,
    currentItem: any,
}

const minPort = 1;
const maxPort = 65535;

export const LDAPAccountDetails: FC<LDAPAccountDetailsProps> = ({ isAccountPopulated, currentItem }) => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const { setFieldValue, values, errors, dirty } = useFormikContext<any>();
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    /**
    * The account is queried using saved account details only. Therefore, to query the account:
    * 1. The account details must be populated.
    * 2. There must be no unsaved changes.
    * @returns 
    */
    const canTestAccount = () => {
        return isAccountPopulated && !dirty;
    }

    const onSslChanged = (event) => {
        const isChecked = event.target.checked;
        setFieldValue("sslEnabled", isChecked)
    }

    const onTlsChanged = (event) => {
        const isChecked = event.target.checked;
        setFieldValue("tlsEnabled", isChecked)
    }

    const onAnonymousConnectionChanged = (event) => {
        const isChecked = event.target.checked;
        setFieldValue("anonymousConnection", isChecked)
    }

    const errorHandler = (accessor) => {
        const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }
            , errors as any);
        if (error) {
            return <span className='text-red-600 text-xs'>{error}</span>
        } else {
            return null;
        }
    };

    const testConnection = () => {
        if (dirty) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.unsavedChanges), message: translate.byKey(TranslationKeys.saveChangesBeforeTesting) });
            return;
        }

        setBusy(true);
        const command: ExecuteCommand = {
            cmd: Commands.Account.label,
            cmdSubType: AccountSubCmds.Test_Account,
            entityId: currentItem.id,
            jsonResponse: ""
        }

        executeCommand(command).then((response: any) => {
            if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {

                if (values.enabled === false) {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToDisabledAccount));
                }
                else {
                    toast.success(translate.byKey(TranslationKeys.successConnectedToAccount));
                }
            }
            else {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.testAccountFailed) });
            }
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    const onValidatePassword = (value: string) => {

        // Ignores validation only if password is already captured and no new edits are made
        if (currentItem?.isPasswordCaptured && !value) {
            return null;
        }

        // If this is either the initial capture of the password or an edit, validate it
        return validatePassword(value, translate, currentItem, 2);
    }

    return <ActivityFrame busy={busy} className='flex flex-col space-y-4 flex-grow bg-white'>
        {/* Connection */}
        <div>
            <div className="mb-3 text-portal-blue-primary-shade2 text-md font-semibold">
                {translate.byKey(TranslationKeys.connection)}:
            </div>
            <div className="flex flex-row mt-2">
                {/* Host input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="hostLabel">
                        <Label htmlFor='details.host.value' required
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.host)}:</Label>
                        {errorHandler("details.host.value")}
                    </div>
                    <Field
                        name="details.host.value"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
                {/* Port input field */}
                <div className="flex flex-col w-1/6">
                    <div className="flex flex-row justify-between" id="portLabel">
                        <Label htmlFor='details.port.value' required
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.port)}:</Label>
                        {errorHandler("details.port.value")}
                    </div>
                    <Field
                        name="details.port.value"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input"
                        validate={(value) => validateIntegerWithinBounds(value, minPort, maxPort, translate, true)} />
                </div>
            </div>
            <div className="flex flex-row mt-2 items-end">
                {/* Base input field */}
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="baseLabel">
                        <Label htmlFor='details.base.value' required
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.base)}:</Label>
                        {errorHandler("details.base.value")}
                    </div>
                    <Field
                        name="details.base.value"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
                {/* Fetch DNs Button */}
                {/* Hidden for now */}
                <div className="hidden flex-col pr-3">
                    <Button variant="tertiary" shape="rounded" className="py-1 px-4 w-fit" onClick={() => { }}>
                        {translate.byKey(TranslationKeys.fetchDNs)}
                    </Button>
                </div>
            </div>
            {/* SSL and TLS Checkboxes */}
            {/* Hidden for now */}
            <div className='hidden flex-row space-x-6 mt-4'>
                {/* SSL */}
                <div className="flex flex-row">
                    <Field
                        id={`checkbox-sslEnabled`}
                        type="checkbox"
                        name="details.sslEnabled.value"
                        checkbox={values.sslEnabled}
                        onChange={onSslChanged} />
                    <Label htmlFor={`checkbox-sslEnabled`} className="ml-2">{translate.byKey(TranslationKeys.ssl)}</Label>
                </div>
                {/* TLS */}
                <div className="flex flex-row">
                    <Field
                        id={`checkbox-tlsEnabled`}
                        type="checkbox"
                        name="details.tlsEnabled.value"
                        checkbox={values.tlsEnabled}
                        onChange={onTlsChanged} />
                    <Label htmlFor={`checkbox-tlsEnabled`} className="ml-2">{translate.byKey(TranslationKeys.tls)}</Label>
                </div>
            </div>
        </div>
        {/* Account */}
        <div>
            <div className="mb-3 text-portal-blue-primary-shade2 text-md font-semibold">
                {translate.byKey(TranslationKeys.account)}:
            </div>
            {/* Username input field */}
            <div className="flex flex-row mt-2">
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="username">
                        <Label htmlFor='details.username.value' required
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.username)}:</Label>
                        {errorHandler("details.username.value")}
                    </div>
                    <Field
                        name="details.username.value"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => validateUsername(value, translate)} />
                </div>
                <div className="flex flex-col flex-1 pr-3">
                    {/* Placeholder to give extra empty column */}
                </div>
            </div>
            {/* Password input field */}
            <div className="flex flex-row mt-2">
                <div className="flex flex-col w-1/3 pr-3">
                    <div className="flex flex-row justify-between" id="password">
                        <Label htmlFor='tempPassword' required
                            className="text-xs text-gray-600">{translate.byKey(TranslationKeys.password)}:</Label>
                        {errorHandler("tempPassword")}
                    </div>
                    <div className="flex flex-row justify-between">
                    <Field
                        type={showPassword ? 'text' : 'password'}
                        name="tempPassword"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => onValidatePassword(value)}
                        placeholder={(currentItem?.isPasswordCaptured) ? "********" : translate.byKey(TranslationKeys.enterNewPassword)} />
                    <div className="flex relative w-6 ml-1">
                        <FontAwesomeIcon
                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                            onClick={() => setShowPassword(showPassword => !showPassword)}
                            icon={showPassword ? faEye : faEyeSlash} />
                    </div>
                    </div>
                </div>
                <div className="flex flex-col flex-1 pr-3">
                    {/* Placeholder to give extra empty column */}
                </div>
            </div>
            {/* Anonymous Connection Checkbox */}
            {/* Hidden for now */}
            <div className="hidden flex-row mt-4">
                <Field
                    id={`checkbox-anonymousConnection`}
                    type="checkbox"
                    name="details.anonymousConnection.value"
                    checkbox={values.anonymousConnection}
                    onChange={onAnonymousConnectionChanged} />
                <Label htmlFor={`checkbox-anonymousConnection`} className="ml-2">{translate.byKey(TranslationKeys.anonymousConnection)}</Label>
            </div>
        </div>
        <div className='pb-4'>
            <Button
                variant="tertiary"
                shape="rounded"
                className="h-8 px-2 mt-1"
                disabled={!canTestAccount()}
                onClick={testConnection}>
                {translate.byKey(TranslationKeys.testAccount)}
            </Button>
        </div>
    </ActivityFrame>
};
 